import invariant from 'invariant';
import { useCallback } from 'react';

import type { ApiFilestackImageOrVideoDataWithCaption } from 'api/types/ApiFilestackImageData';
import { useI18n } from 'i18n/hooks/useI18n';
import { showModal } from 'zustand-stores/modalStore';

type Props = {
  altTextRequired?: boolean;
  values: ApiFilestackImageOrVideoDataWithCaption[] | null | undefined;
  onChange: (arg0: ApiFilestackImageOrVideoDataWithCaption[]) => void;
  allowSVG?: boolean;
  allowVideo?: boolean;
  extraAnalyticsData?: Record<string, unknown>;
};

export function useMultiImagePickerEditImage({
  altTextRequired,
  values,
  onChange,
  allowSVG,
  allowVideo,
  extraAnalyticsData,
}: Props) {
  const { i18n } = useI18n();

  const editImage = useCallback(
    (index: number) => {
      invariant(values, 'Missing image to edit');

      const {
        altText: currentAltText,
        caption: currentCaption,
        ...remainingData
      } = values[index];

      showModal('ADD_OR_EDIT_IMAGE', {
        image: remainingData.type === 'IMAGE' ? remainingData : undefined,
        altText: currentAltText,
        altTextRequired,
        caption: currentCaption,
        extraAnalyticsData,
        videoUrl:
          remainingData.type === 'VIDEO' ? remainingData.videoUrl : undefined,
        type: remainingData.type,
        allowSVG,
        allowVideo,
        saveImage: ({ image, altText, caption, videoUrl, type = 'IMAGE' }) => {
          const errors: { altText?: string } = {};

          if (type === 'IMAGE' && !altText && altTextRequired) {
            errors.altText = i18n('Required');
            return errors;
          }

          const newValues = [...values];
          newValues[index] = {
            ...image,
            altText,
            caption,
            videoUrl,
            type,
          } as ApiFilestackImageOrVideoDataWithCaption;

          onChange(newValues);

          return errors;
        },
      });
    },
    [
      i18n,
      values,
      altTextRequired,
      extraAnalyticsData,
      allowSVG,
      allowVideo,
      onChange,
    ],
  );

  return { editImage };
}
